import React, { useState, FormEvent, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { resetPassword } from "../../api";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { images } from "../../assets/images";
import { useNightMode } from "../../contexts/NightModeContext";

/** Reset password — same API flow, glass auth card look */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isNightMode } = useNightMode();
  const token: string | null = searchParams.get("token");

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  useEffect(() => {
    document.body.classList.add('ex-theme');
  }, []);

  const handleReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (!token) {
      toast.error("Invalid or missing token.");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      toast.success("Password has been reset successfully. You may now log in.");
      setSubmitted(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to reset password.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ex-auth-screen">
      <div className="auth-glow-1" aria-hidden="true" />
      <div className="auth-glow-2" aria-hidden="true" />

      <form onSubmit={handleReset} className="ex-auth-card glass-panel shadow-premium">
        <div className="auth-logo">
          <img
            src={isNightMode ? images.eventox_logo_dark : images.eventox_light}
            alt="Eventox"
          />
        </div>
        <p className="auth-subtitle">Reset Password</p>

        <div className="form-group mb-3">
          <label htmlFor="new-password">New Password</label>
          <input
            id="new-password"
            type="password"
            className="form-control"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitted}
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            id="confirm-password"
            type="password"
            className="form-control"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitted}
          />
        </div>

        <button
          type="submit"
          disabled={loading || submitted}
          className="ex-auth-submit"
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>

        <p className="ex-auth-footer">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="btn btn-link"
            style={{ color: "var(--ex-brand-start)", textDecoration: "underline" }}
          >
            Back to Login
          </button>
        </p>
      </form>

      <ToastContainer />
    </div>
  );
};

export default ResetPassword;
